import {api} from "../../lib/api";
import {
  CardStatus,
  getStatusForCard,
  isCardBlocked,
  isCardInReview,
} from "../../lib/card-status-utils";
import performArchiveAction from "./performArchiveAction";
import {workflowStyles} from "./unified-card-container.css";
import {TooltipForChild, cx} from "@cdx/common";
import {performStartAction} from "./performStartAction";
import {cardStatusVariants} from "@cdx/ds/components/DSCard/DSCardTheme.css";
import {
  DSIconArchive,
  DSIconBlocked,
  DSIconButton,
  DSIconCheck,
  DSIconPlay,
  DSIconReview,
  DSIconSnoozing,
  Row,
} from "@cdx/ds";
import {getCurrentTimeTrackingSegment} from "../time-tracking/time-tracking-utils";
import {ReactNode} from "react";
import {Card, CardId} from "../../cdx-models/Card";
import {Root} from "../../cdx-models/Root";
import {performDoneAction} from "./performDoneAction";

type WorkflowStatus = "started" | "done" | "snoozing" | "blocked" | "review" | "archived";

export const WorkflowButton = ({
  icon,
  status,
  active,
  onClick,
  tooltip,
  disabled,
  canModify,
}: {
  icon: ReactNode;
  status: WorkflowStatus;
  active?: boolean;
  onClick?: () => void;
  tooltip?: string;
  disabled?: boolean;
  canModify?: boolean | null;
}) => {
  const getTooltip = () => {
    if (onClick && !canModify) return "You don't have permission to change the Card's state";
    return tooltip;
  };
  return (
    <TooltipForChild tooltip={getTooltip} delayed={!disabled} targetIsDisabled={disabled}>
      <DSIconButton
        variant={null}
        size="md"
        icon={icon}
        negatePadding
        disabled={(onClick && !canModify) || disabled}
        onClick={onClick}
        className={cx(
          cardStatusVariants[status],
          workflowStyles.nextButton.base,
          active && workflowStyles.nextButton.active
        )}
        skipSuccessIcon
      />
    </TooltipForChild>
  );
};

const PlayButton = ({
  card,
  status,
  canModify,
  root,
  blockOrReviewTooltip,
}: {
  card: Card;
  status: CardStatus;
  canModify: boolean | null;
  root: Root;
  blockOrReviewTooltip: string | null;
}) => {
  const cardId = card.$meta.get("cardId", null);
  if (status !== "started") {
    const shouldTrack = root.account.timeTrackingMode === "strict";
    const getTooltip = () => {
      if (blockOrReviewTooltip) return blockOrReviewTooltip;
      if (shouldTrack) {
        return "Start working on this Card and track time.";
      } else {
        return "Start working on this Card.";
      }
    };
    return (
      <WorkflowButton
        tooltip={getTooltip()}
        disabled={Boolean(blockOrReviewTooltip)}
        status="started"
        icon={<DSIconPlay />}
        onClick={async () => {
          await performStartAction({cardIds: [cardId]});
        }}
        canModify={canModify}
      />
    );
  } else {
    const currSeg = getCurrentTimeTrackingSegment(root);
    const getTooltip = () => {
      if (currSeg?.card.cardId === cardId) {
        return "Click again to stop this Card and its time tracking.";
      } else {
        return "Click again to signal that this Card is no longer in progress.";
      }
    };
    return (
      <WorkflowButton
        tooltip={getTooltip()}
        status="started"
        active
        onClick={() => api.mutate.cards.update({id: cardId, status: "not_started"})}
        canModify={canModify}
        icon={<DSIconPlay />}
      />
    );
  }
};

type ButtonAreaProps = {
  card: Card;
  canModify: boolean | null;
  onCreateConvo: (type: string) => void;
  openConvoPanel: () => void;
  onCardDone?: () => Promise<unknown>;
  mayGuardCard: boolean;
  root: Root;
};

export const ButtonArea = (props: ButtonAreaProps) => {
  if (process.env.REACT_APP_MODE === "open") return null;
  const {card, canModify, onCreateConvo, openConvoPanel, onCardDone, mayGuardCard, root} = props;
  const isBlocked = isCardBlocked(card);
  const isReviewed = isCardInReview(card);
  const status = getStatusForCard(card);

  const cardId = card.$meta.get("cardId", null) as CardId;

  const handleDone = () => {
    if (card.status === "done") {
      return api.mutate.cards.update({id: cardId, status: "not_started"});
    } else {
      return performDoneAction({cardIds: [cardId], onCardDone});
    }
  };

  const blockOrReviewTooltip = isBlocked
    ? "Unblock this Card before continuing."
    : isReviewed
      ? "Finish the review before continuing."
      : null;

  const getDoneTooltip = () => {
    if (!mayGuardCard) {
      return "Only Guardians may set a card as done.";
    }
    if (blockOrReviewTooltip) return blockOrReviewTooltip;
    if (card.status === "done") {
      return "Work not complete? Click again to undo it.";
    }
    return "Mark this Card as done.";
  };

  return (
    <Row sp="12px" align="center">
      {card.status === "snoozing" ? (
        <WorkflowButton
          tooltip="This Card is snoozing. Click to unsnooze."
          status="snoozing"
          onClick={() => api.mutate.cards.update({id: cardId, status: "started"})}
          canModify={canModify}
          active
          icon={<DSIconSnoozing />}
        />
      ) : (
        <PlayButton
          card={card}
          status={status}
          canModify={canModify}
          root={root}
          blockOrReviewTooltip={blockOrReviewTooltip}
        />
      )}
      <WorkflowButton
        tooltip={
          isReviewed
            ? "You need to finish the review before being able to block this Card."
            : isBlocked
              ? "This Card is blocked."
              : "Unable to complete this work? Tell co-workers you're blocked."
        }
        status="blocked"
        active={isBlocked}
        disabled={!canModify || isReviewed}
        onClick={() => {
          if (!isBlocked) {
            onCreateConvo("block");
          } else {
            openConvoPanel();
          }
        }}
        canModify={canModify}
        icon={<DSIconBlocked />}
      />
      <WorkflowButton
        tooltip={
          isBlocked
            ? "Unblock this Card before continuing."
            : isReviewed
              ? "This Card is in review."
              : "Mark card as to be reviewed."
        }
        status="review"
        active={isReviewed}
        disabled={!canModify || isBlocked}
        onClick={() => {
          if (!isReviewed) {
            onCreateConvo("review");
          } else {
            openConvoPanel();
          }
        }}
        canModify={canModify}
        icon={<DSIconReview />}
      />
      <WorkflowButton
        tooltip={getDoneTooltip()}
        status="done"
        disabled={Boolean(blockOrReviewTooltip) || !mayGuardCard}
        onClick={handleDone}
        canModify={canModify}
        icon={<DSIconCheck />}
        active={status === "done"}
      />
      {card.status === "done" ? (
        <WorkflowButton
          tooltip="Archive this Card."
          status="archived"
          onClick={() => performArchiveAction(card)}
          canModify={canModify}
          icon={<DSIconArchive />}
        />
      ) : null}
    </Row>
  );
};
